import { Formik } from "formik";
import React, { useContext, useEffect, useState } from "react";
import { FiTrash2 } from "react-icons/fi";
import Loader from "react-loader-spinner";
import { useDispatch, useSelector } from "react-redux";
import MaskedInput from "react-text-mask";
import { FiRefreshCw, FiX } from "react-icons/fi";
import Error from "../../components/Error";
import api from "../../services/api";
import { Creators as AddressActions } from "../../store/ducks/address";
import { Creators as ErrorActions } from "../../store/ducks/error";
import { Creators as DeliveryContextActions } from "../../store/ducks/deliveryContext";
import {
  AddressForm,
  AddressesManagement,
  ButtonFooterWrap,
  CancelBtn,
  Container,
  FieldErrorMessage,
  Form,
  FormGroup,
  InputField,
  Label,
  LoaderContainer,
  RemoveAddressButton,
  RemoveAddressConfirmation,
  SubmitBtn,
  UpdateAddressButton
} from "./styles";
import { AddressSchema } from "./validation";
import { getAddressByCEP } from "../../services/cep";
import { cepMask, phoneMask } from "../../util/mask";
import { LoadingOverlayContext } from "../../contexts/LoadingOverlayContext";

export default function Addresses() {
  const dispatch = useDispatch();
  const { setLoadingOverlay } = useContext(LoadingOverlayContext);
  const [addresses, setAddresses] = useState([]);
  const [modal, setModal] = useState(false);
  const [modalNewAddress, setModalNewAddress] = useState(false);
  const [addressToRemove, setAddressToRemove] = useState(null);
  const { loading: loadingUser } = useSelector(state => state.user);
  const uuid = useSelector(state => state.user.profile?.uuid);
  const [loadingAddresses, setLoadingAddresses] = useState(true);
  const [loading, setLoading] = useState({ loading: false, message: "" });
  const [initialValues, setInitialValues] = useState(null);
  const [addressId, setAddressId] = useState(null);
  const [isUpdate, setIsUpdate] = useState(false);
  const [isCepValidated, setIsCepValidated] = useState(false);
  const [title, setTitle] = useState("");

  useEffect(() => {
    setTitle("Meus Endereços");
    getAddresses();
  }, [loadingUser, uuid]);

  async function getAddresses() {
    if (!loadingUser) {
      setLoadingAddresses(true);
      setLoadingOverlay(true);
      try {
        const response = await api.get(`/customers/${uuid}/address?_limit=100`);
        setAddresses(response.data);
      } finally {
        setLoadingAddresses(false);
        setLoadingOverlay(false);
      }
    }
  }

  async function handleRemoveAddress() {
    try {
      await api.delete(`customers/${uuid}/address/${addressToRemove.id}`);
      const addressesUpdated = addresses.filter(
        address => address.id !== addressToRemove.id
      );
      setAddresses(addressesUpdated);
      // Excluir o endereco ativo devolve o contexto de entrega a heranca e, na ausencia de
      // alternativa, ao estado indefinido. Nao ha action de delete no duck address -- esta
      // pagina chama a API direto -- entao o aviso parte daqui.
      dispatch(DeliveryContextActions.addressRemoved(addressToRemove.id));
      setModal(false);
    } catch (err) {
      dispatch(ErrorActions.setError("address-remove", err.response.data.user));
      setModal(false);
    }
  }

  async function handleSetSelectedAddress(id) {
    setIsUpdate(id !== 0)

    if (id !== 0) {
      setAddressId(id)
      setIsCepValidated(true);
      setTitle("Meu endereço");
      setLoadingOverlay(true);
      const { data } = await api.get(
        `/customers/${uuid}/address/${id}`
      ).finally(() => setLoadingOverlay(false));
      setInitialValues({
        nome_contato: data.nome_contato,
        telefone_contato: data.telefone_contato,
        cep: data.cep,
        uf: data.cidade.uf_sigla,
        cidade: data.cidade.nome,
        bairro: data.bairro,
        numero: data.numero,
        rua: data.endereco,
        complemento: data.complemento,
        ibge: data.cidade.ibge_id
      });
      setIsUpdate(true);
    } else {
      setIsCepValidated(false);
      setTitle("Adicionar novo endereço");
      setInitialValues({
        nome_contato: "",
        telefone_contato: "",
        cep: "",
        uf: "",
        cidade: "",
        bairro: "",
        numero: "",
        rua: "",
        complemento: "",
        ibge: ""
      });
    }

    setModalNewAddress(true);
  }

  async function handleCloseSelectedAddress() {
    setModalNewAddress(false);
  }

  function handleSetAddressModal(address) {
    setAddressToRemove(address);
    setModal(true);
  }

  function clearAddressFields(setFieldValue) {
    setFieldValue("uf", "");
    setFieldValue("cidade", "");
    setFieldValue("rua", "");
    setFieldValue("bairro", "");
    setFieldValue("ibge", "");
  }

  function fillAddressFields(data, setFieldValue) {
    setFieldValue("uf", data.uf || "");
    setFieldValue("cidade", data.localidade || "");
    setFieldValue("rua", data.logradouro || "");
    setFieldValue("bairro", data.bairro || "");
    setFieldValue("ibge", data.ibge || "");
  }

  async function fetchAddressByCEPAndHandleForm(cep, formikHelpers) {
    const { setFieldValue, setFieldError, setFieldTouched } = formikHelpers;
    setLoading({ loading: true, message: "Buscando CEP..." });
    setLoadingOverlay(true);

    try {
      const { data } = await getAddressByCEP(cep);

      if (data.erro) {
        clearAddressFields(setFieldValue);
        setIsCepValidated(false);
        setFieldTouched("cep", true, false);
        setFieldError("cep", "O CEP informado é inexistente.");
        return false;
      }

      fillAddressFields(data, setFieldValue);
      setIsCepValidated(true);
      setFieldError("cep", undefined);
      return true;
    } catch (error) {
      clearAddressFields(setFieldValue);
      setIsCepValidated(false);
      setFieldTouched("cep", true, false);
      setFieldError("cep", "Erro ao buscar CEP. Tente novamente.");
      return false;
    } finally {
      setLoading({ loading: false, message: "" });
      setLoadingOverlay(false);
    }
  }

  function handleSubmit(values) {
    const {
      nome_contato,
      telefone_contato,
      cep,
      uf,
      cidade,
      bairro,
      numero,
      rua,
      complemento,
      ibge
    } = values;

    const telefone = telefone_contato.replace(/[^a-zA-Z0-9]/g, "");
    const cepWithoutMask = cep.replace(/[^a-zA-Z0-9]/g, "");

    if (!isUpdate) {
      dispatch(
        AddressActions.addressStoreRequest(
          {
            nome_contato,
            telefone_contato: telefone,
            cep: cepWithoutMask,
            uf,
            cidade,
            bairro,
            numero,
            rua,
            complemento,
            ibge
          },
          false
        )
      );
      getAddresses();
      setModalNewAddress(false);
      return;
    }

    dispatch(
      AddressActions.addressUpdateRequest({
        nomeContato: nome_contato,
        telefoneContato: telefone,
        cep: cepWithoutMask,
        uf,
        cidade,
        bairro,
        numero,
        rua,
        complemento,
        ibge,
        addressId
      })
    );
    getAddresses();
    setModalNewAddress(false);
  }

  return (
    <Container>
      {!loadingAddresses && addresses && addresses.map(address => (
        <AddressesManagement key={address.id}>
          <div className="address-data">
            <div className="address-card-owner">
              <strong>{address.nome_contato}</strong>
              <span>Responsável</span>
            </div>

            <div className="address-card-data">
              <p>
                {address.endereco}, {address.bairro} {address.complemento},{" "}
                {address.numero} - {address.cidade.nome}/{address.cidade.uf_sigla}{" "}
              </p>
              <p>
                CEP: {address.cep.replace(/(\d{5})(\d{3})/g, "$1-$2")}
              </p>
            </div>
          </div>
          <div className="address-buttons">
            <UpdateAddressButton onClick={() => handleSetSelectedAddress(address.id)}>
              <FiRefreshCw size={16} style={{ marginRight: 6 }} />
              Atualizar Endereço
            </UpdateAddressButton>

            <RemoveAddressButton onClick={() => handleSetAddressModal(address)}>
              <FiTrash2 />
              Remover
            </RemoveAddressButton>
          </div>
        </AddressesManagement>
      ))}
      {loadingAddresses && (
        <LoaderContainer>
          <Loader type="ThreeDots" color="#00b2a9" height={40} width={40} />
        </LoaderContainer>
      )}
      <p>
        {addresses.length === 0 && !loadingAddresses && "Nenhum endereço cadastrado"}
      </p>
      <Error errorKey="address-remove" style={{ marginBottom: 30 }} />
      <div className="address-panel-header">
        <ButtonFooterWrap
          id="button-finalizar-pagamento"
          onClick={() => handleSetSelectedAddress(0)}
          type="button">
          Adicionar novo endereço
        </ButtonFooterWrap>
      </div>

      {modalNewAddress && (
        <AddressForm>
          <div>
            <div>
              <FiX size={24} onClick={handleCloseSelectedAddress} style={{ color: "var(--neutral-900)", cursor: "pointer" }} />
              <h1>{title}</h1>
            </div>
            <Error errorKey="create-update-address" style={{ marginBottom: 20 }} />
            {initialValues && (
              <Formik
                initialValues={initialValues}
                validationSchema={AddressSchema}
                validateOnChange={false}
                onSubmit={handleSubmit}
              >
                {({
                  values,
                  handleChange,
                  handleBlur,
                  errors,
                  touched,
                  handleSubmit,
                  setFieldValue,
                  setFieldError,
                  setFieldTouched,
                  validateForm,
                  handleReset,
                  isSubmitting,
                  isValid
                }) => (
                  <>
                    <Form onSubmit={handleSubmit}>

                      <div className="groupLine">

                        <FormGroup className="smallField">
                          {errors.cep && touched.cep ? (
                            <FieldErrorMessage>{errors.cep}</FieldErrorMessage>
                          ) : null}

                          <MaskedInput
                            invalid={errors.cep && touched.cep}
                            id="cep"
                            type="text"
                            name="cep"
                            mask={cepMask}
                            onChange={async (e) => {
                              const oldCEP = (values.cep || "").replace(/[^a-zA-Z0-9]/g, "");
                              const nextCEP = (e.target.value || "").replace(/[^a-zA-Z0-9]/g, "");

                              setIsCepValidated(false);
                              handleChange(e);

                              if (nextCEP.length === 8 && oldCEP !== nextCEP) {
                                const cepLoaded = await fetchAddressByCEPAndHandleForm(nextCEP, {
                                  setFieldValue,
                                  setFieldError,
                                  setFieldTouched
                                });

                                if (cepLoaded) {
                                  await validateForm();
                                }
                              }
                            }}
                            value={values.cep}
                            onBlur={() => setFieldTouched("cep", true, false)}
                            placeholder="Ex: _____-___"
                            render={(ref, props) => (
                              <InputField ref={input => ref(input)} {...props} />
                            )}
                          />

                          <Label>CEP</Label>
                        </FormGroup>

                      </div>

                      {isCepValidated && (
                        <>
                      <div className="groupLine">

                        <FormGroup>
                          {errors.rua && touched.rua ? (
                            <FieldErrorMessage>{errors.rua}</FieldErrorMessage>
                          ) : null}

                          <InputField
                            invalid={errors.rua && touched.rua}
                            id="rua"
                            type="text"
                            name="rua"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.rua}
                            placeholder="Ex: Rua/Av. Fulano da Silva"
                          />
                          <Label>Rua/Avenida</Label>
                        </FormGroup>

                        <FormGroup className="smallField marginLeft">
                          {errors.numero && touched.numero ? (
                            <FieldErrorMessage>{errors.numero}</FieldErrorMessage>
                          ) : null}

                          <InputField
                            invalid={errors.numero && touched.numero}
                            id="numero"
                            type="text"
                            name="numero"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.numero}
                            placeholder="Ex: 1191 ou s/n"
                          />
                          <Label>Número</Label>
                        </FormGroup>

                      </div>

                      <div className="groupLine">

                        <FormGroup>
                          {errors.cidade && touched.cidade ? (
                            <FieldErrorMessage>{errors.cidade}</FieldErrorMessage>
                          ) : null}

                          <InputField
                            disabled
                            invalid={errors.cidade && touched.cidade}
                            id="cidade"
                            type="text"
                            name="cidade"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.cidade}
                            placeholder="Ex: Tubarão"
                          />
                          <Label>Cidade</Label>
                        </FormGroup>

                        <FormGroup className="smallField marginLeft">
                          {errors.uf && touched.uf ? (
                            <FieldErrorMessage>{errors.uf}</FieldErrorMessage>
                          ) : null}

                          <InputField
                            invalid={errors.uf && touched.uf}
                            id="uf"
                            type="uf"
                            name="text"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled
                            value={values.uf}
                            placeholder="Ex: SC"
                          />
                          <Label>UF</Label>
                        </FormGroup>

                      </div>

                      <FormGroup>
                        {errors.bairro && touched.bairro ? (
                          <FieldErrorMessage>{errors.bairro}</FieldErrorMessage>
                        ) : null}

                        <InputField
                          invalid={errors.bairro && touched.bairro}
                          id="bairro"
                          type="text"
                          name="bairro"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.bairro}
                          placeholder="Ex: Santo Antonio de Pádua"
                        />
                        <Label>Bairro</Label>
                      </FormGroup>

                      <FormGroup>
                        {errors.complemento && touched.complemento ? (
                          <FieldErrorMessage>
                            {errors.complemento}
                          </FieldErrorMessage>
                        ) : null}

                        <InputField
                          invalid={errors.complemento && touched.complemento}
                          id="complemento"
                          type="text"
                          name="complemento"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.complemento}
                          placeholder="Ex: Andar/Apartamento (opcional)"
                        />
                        <Label>Complemento</Label>
                      </FormGroup>

                      <div className="groupLine">
                        <FormGroup>
                          {errors.nome_contato && touched.nome_contato ? (
                            <FieldErrorMessage>
                              {errors.nome_contato}
                            </FieldErrorMessage>
                          ) : null}

                          <InputField
                            invalid={errors.nome_contato && touched.nome_contato}
                            id="nome_contato"
                            type="text"
                            name="nome_contato"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.nome_contato}
                            placeholder="Ex: Fulano da Silva"
                          />
                          <Label>Nome para contato</Label>
                        </FormGroup>

                        <FormGroup className="marginLeft">
                          {errors.telefone_contato && touched.telefone_contato ? (
                            <FieldErrorMessage>
                              {errors.telefone_contato}
                            </FieldErrorMessage>
                          ) : null}

                          <MaskedInput
                            name="telefone_contato"
                            type="text"
                            placeholder="Ex: (__) ____-____"
                            mask={phoneMask}
                            invalid={
                              errors.telefone_contato && touched.telefone_contato
                            }
                            id="registerNumber"
                            value={values.telefone_contato}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            render={(ref, props) => (
                              <InputField ref={input => ref(input)} {...props} />
                            )}
                          />

                          <Label>Telefone Contato</Label>
                        </FormGroup>
                      </div>

                      <div className="btnArea">
                        <SubmitBtn disabled={!isValid || loading.loading} type="submit">
                          {loading.loading ? loading.message : "Salvar alterações"}
                        </SubmitBtn>
                        <CancelBtn onClick={handleCloseSelectedAddress}>
                          Cancelar
                        </CancelBtn>
                      </div>
                        </>
                      )}
                    </Form>
                  </>
                )}
              </Formik>
            )}
          </div>
        </AddressForm>
      )}

      {modal && (
        <RemoveAddressConfirmation>
          <div>
            <div className="content">
              <h1>Excluir endereço</h1>

              <p>Você realmente deseja excluir este endereço?</p>
            </div>

            <div className="footer">
              <button onClick={() => setModal(false)} className="cancel-btn">
                Cancelar
              </button>
              <button
                onClick={() => handleRemoveAddress()}
                className="remove-btn"
              >
                Sim, desejo
              </button>
            </div>
          </div>
        </RemoveAddressConfirmation>
      )}
    </Container>
  );
}
